import { useEffect, useRef, useState } from "react";

const MAX_INDENT = 240;
const TICK = 40;

export const EditorRuler = ({ left, right, onChange }) => {
    const trackRef = useRef(null);
    const dragRef = useRef(null);
    const [drag, setDrag] = useState(null);

    const clamp = (v) => Math.max(0, Math.min(MAX_INDENT, v));

    useEffect(() => {
        if (!drag) return;
        const move = (e) => {
            const side = dragRef.current;
            if (!side) return;
            const rect = trackRef.current.getBoundingClientRect();
            const fromLeft = e.clientX - rect.left;
            if (side === "left") {
                onChange([clamp(Math.round(fromLeft)), right]);
            } else {
                onChange([left, clamp(Math.round(rect.width - fromLeft))]);
            }
        };
        const up = () => {
            dragRef.current = null;
            setDrag(null);
        };
        window.addEventListener("pointermove", move);
        window.addEventListener("pointerup", up);
        return () => {
            window.removeEventListener("pointermove", move);
            window.removeEventListener("pointerup", up);
        };
    }, [drag, left, right, onChange]);

    const startDrag = (e, side) => {
        e.preventDefault();
        dragRef.current = side;
        setDrag(side);
    };

    const trackClick = (e) => {
        const rect = trackRef.current.getBoundingClientRect();
        const fromLeft = e.clientX - rect.left;
        const distLeft = Math.abs(fromLeft - left);
        const distRight = Math.abs((rect.width - fromLeft) - right);
        if (distLeft <= distRight) {
            onChange([clamp(Math.round(fromLeft)), right]);
        } else {
            onChange([left, clamp(Math.round(rect.width - fromLeft))]);
        }
    };

    const diamond = (side) => (
        <>
            <span className={`w-3 h-3 rotate-45 mt-0.5 ${drag === side ? "bg-gold" : "bg-gold/70"}`} />
            <span className={`flex-1 w-0.5 ${drag === side ? "bg-gold" : "bg-gold/50"}`} />
        </>
    );

    return (
        <div data-testid="editor-ruler" className="select-none">
            <div className="flex justify-between text-[10px] uppercase tracking-[0.15em] text-slate-500 mb-1.5">
                <span data-testid="ruler-left-value">Recuo esq.: {left}px</span>
                <span className="text-slate-600">arraste os marcadores ou clique na régua</span>
                <span data-testid="ruler-right-value">Recuo dir.: {right}px</span>
            </div>
            <div
                ref={trackRef}
                onPointerDown={(e) => { if (e.target === e.currentTarget) trackClick(e); }}
                className="relative h-7 border border-white/10 bg-ink overflow-hidden"
            >
                {Array.from({ length: 19 }).map((_, i) => (
                    <span
                        key={i}
                        className={`absolute top-0 w-px pointer-events-none ${i % 2 === 0 ? "h-3 bg-white/20" : "h-2 bg-white/10"}`}
                        style={{ left: i * TICK }}
                    />
                ))}
                <span className="absolute top-0 bottom-0 left-0 bg-gold/[0.08] pointer-events-none" style={{ width: left }} />
                <span className="absolute top-0 bottom-0 right-0 bg-gold/[0.08] pointer-events-none" style={{ width: right }} />
                <button
                    type="button"
                    data-testid="ruler-left-handle"
                    onPointerDown={(e) => startDrag(e, "left")}
                    className="absolute top-0 bottom-0 w-5 -ml-2.5 cursor-ew-resize flex flex-col items-center touch-none z-10"
                    style={{ left: Math.max(left, 10) }}
                    title="Arrastar recuo esquerdo"
                >
                    {diamond("left")}
                </button>
                <button
                    type="button"
                    data-testid="ruler-right-handle"
                    onPointerDown={(e) => startDrag(e, "right")}
                    className="absolute top-0 bottom-0 w-5 -ml-2.5 cursor-ew-resize flex flex-col items-center touch-none z-10"
                    style={{ left: `calc(100% - ${Math.max(right, 10)}px)` }}
                    title="Arrastar recuo direito"
                >
                    {diamond("right")}
                </button>
            </div>
        </div>
    );
};
